import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, HandCoins, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AddLeadModal from "@/components/sales/AddLeadModal";
import LeadCaptureDialog from "@/components/sales/LeadCaptureDialog";
import LeadDetailDrawer from "@/pages/sales/LeadDetailDrawer";
import PendingQueueBanner from "@/components/PendingQueueBanner";
import { STAGE_LABEL, STAGE_COLOR } from "@/lib/sales";
import { isSalesManagerRole, isSalesExecutiveRole } from "@/lib/roleAliases";

/**
 * Feb 2026 — single ledger component powering both:
 *   • /sales/leads              → variant="main"      (excludes Marketing Handoff)
 *   • /sales/marketing-leads    → variant="marketing" (only Marketing Handoff)
 *
 * Both variants share status cards, a per-month filter (defaults to the
 * current month), search, and the leads table.
 */

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MARKETING_SOURCE = "Marketing Handoff";

function LeadLedger({ variant = "main" }) {
  const isMarketing = variant === "marketing";
  const { principal, isAdmin, roleName, roleType } = useAuth();
  const [searchParams] = useSearchParams();
  const focusLeadId = searchParams.get("focus_lead");
  const [leads, setLeads] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);

  const isSM = isSalesManagerRole(roleName);
  const canAdd = isAdmin || roleType === "director" || isSM;
  const isExec = isSalesExecutiveRole(roleName);

  const load = async () => {
    setLoading(true);
    try {
      const [l, e, sm] = await Promise.all([
        // Marketing variant hits the unified marketing-ledger feed (pending
        // marketing leads + converted marketing SalesLeads). Main ledger stays
        // on /sales/leads.
        isMarketing ? api.get("/sales/marketing-ledger") : api.get("/sales/leads"),
        api.get("/sales/executives"),
        api.get("/users/sales-managers").catch(() => ({ data: [] })),
      ]);
      setLeads(l.data);
      setExecutives(e.data);
      setSalesManagers(sm.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isMarketing]);

  // Feb 2026 · Smart deep-link — when arriving with ?focus_lead=<id>, wait for
  // the ledger to hydrate, then auto-open the LeadDetailDrawer AND scroll the
  // corresponding row into view.
  useEffect(() => {
    if (!focusLeadId || loading) return;
    const target = leads.find((l) => l.id === focusLeadId);
    if (!target) return;
    setSelected(target);
    const el = document.querySelector(`[data-testid="open-lead-${focusLeadId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const row = el.closest("tr, .iz-card") || el;
      row.classList.add("iz-flash-highlight");
      setTimeout(() => row.classList.remove("iz-flash-highlight"), 2600);
    }
  }, [focusLeadId, loading, leads]);

  const claim = async (id) => {
    try {
      await api.post(`/sales/leads/${id}/claim`);
      toast.success("Assigned to you");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const userById = useMemo(() => {
    const map = {};
    executives.forEach((u) => { map[u.id] = u; });
    salesManagers.forEach((u) => { if (!map[u.id]) map[u.id] = u; });
    if (principal?.id && !map[principal.id]) {
      map[principal.id] = { id: principal.id, name: principal.name || principal.email, email: principal.email };
    }
    return map;
  }, [executives, salesManagers, principal]);

  // Feb 2026 (v2) — backend now classifies purely by creator role, so the
  // client no longer needs to strip rows by `origin_lead_id`. Both variants
  // just render whatever the API returned for the caller's ledger.
  const scopedLeads = leads;

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Month filter: "all" | 0..11 (JS month index). Default = current month.
  const [monthFilter, setMonthFilter] = useState(() =>
    focusLeadId ? "all" : new Date().getMonth()
  );
  // Feb 2026 (v4) — Same-page nav (Pending banner click while already on the
  // ledger) only updates the search param, not the mount. Reset to 'all' every
  // time focus_lead changes so the target row is always in the DOM.
  useEffect(() => {
    if (focusLeadId) setMonthFilter("all");
  }, [focusLeadId]);

  const monthLeads = useMemo(() => {
    if (monthFilter === "all") return scopedLeads;
    return scopedLeads.filter((l) => {
      // Feb 2026 (v3): month bucketing keys on the ORIGINAL creation date so
      // a backdated Lead Date sticks even after subsequent status changes
      // (status transitions overwrite updated_at with 'now', which used to
      // silently re-bucket the row into the current month).
      const ref = l.created_at;
      if (!ref) return false;
      const d = new Date(ref);
      return d.getMonth() === monthFilter;
    });
  }, [scopedLeads, monthFilter]);

  const filtered = useMemo(() => {
    let out = monthLeads;
    if (statusFilter === "unassigned") {
      out = out.filter(l => !l.assigned_to_user_id);
    } else if (statusFilter !== "all") {
      out = out.filter(l => l.status === statusFilter);
    }
    if (q.trim()) {
      const s = q.toLowerCase();
      out = out.filter(l =>
        l.client_name.toLowerCase().includes(s)
        || l.phone.includes(s)
        || (l.source || "").toLowerCase().includes(s)
      );
    }
    return out;
  }, [q, monthLeads, statusFilter]);

  // Summary cards reflect the currently-selected month (per spec).
  const stats = useMemo(() => ({
    total: monthLeads.length,
    unassigned: monthLeads.filter(l => !l.assigned_to_user_id).length,
    contacted: monthLeads.filter(l => l.status === "contacted").length,
    in_discussion: monthLeads.filter(l => l.status === "in_discussion").length,
    pipeline: monthLeads.filter(l => l.status === "pipeline").length,
    won: monthLeads.filter(l => l.status === "won").length,
    loss: monthLeads.filter(l => l.status === "loss").length,
  }), [monthLeads]);

  const monthLabel = monthFilter === "all" ? "All months" : MONTHS[monthFilter];
  const pageTitle = isMarketing ? "Marketing Lead Ledger" : "Lead Ledger";
  const pageSub = isMarketing ? "Sales · Marketing Handoff" : "Sales · Ledger";

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">{pageSub}</p>
          <h1 className="font-display text-3xl text-white mt-1">{pageTitle}</h1>
        </div>
        {canAdd && !isMarketing && (
          <button className="iz-btn-primary flex items-center gap-2" onClick={() => setShowAdd(true)} data-testid="add-lead-button">
            <Plus className="w-4 h-4"/> Add Lead
          </button>
        )}
      </div>

      <div className="px-10 py-8">
        {/* Deep-linked Pending Actions (Feb 2026) */}
        <PendingQueueBanner />

        {/* Month filter row */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="month-filter-row">
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mr-2">Month</span>
          <button onClick={() => setMonthFilter("all")}
                  aria-pressed={monthFilter === "all"}
                  data-testid="month-btn-all"
                  className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border transition-colors ${
                    monthFilter === "all"
                      ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                      : "border-[#262626] text-gray-400 hover:text-white hover:border-[#d4af37]/60"
                  }`}>
            All
          </button>
          {MONTHS.map((m, idx) => {
            const active = monthFilter === idx;
            return (
              <button key={m}
                      onClick={() => setMonthFilter(idx)}
                      aria-pressed={active}
                      data-testid={`month-btn-${m.toLowerCase()}`}
                      className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border transition-colors ${
                        active
                          ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                          : "border-[#262626] text-gray-400 hover:text-white hover:border-[#d4af37]/60"
                      }`}>
                {m}
              </button>
            );
          })}
        </div>

        {/* Status cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
          {[
            { key: "all",           label: "Total Leads",   value: stats.total },
            { key: "unassigned",    label: "Unassigned",    value: stats.unassigned, gold: true },
            { key: "contacted",     label: "Contacted",     value: stats.contacted },
            { key: "in_discussion", label: "In Discussion", value: stats.in_discussion },
            { key: "pipeline",      label: "In Pipeline",   value: stats.pipeline },
            { key: "won",           label: "Won",           value: stats.won, gold: true },
            { key: "loss",          label: "Loss",          value: stats.loss },
          ].map((s) => {
            const active = statusFilter === s.key;
            const toggle = () => setStatusFilter(active && s.key !== "all" ? "all" : s.key);
            return (
              <button key={s.key} onClick={toggle}
                      data-testid={`stat-card-${s.key}`}
                      aria-pressed={active}
                      className={`iz-card p-4 text-left transition-all group ${active ? "border-[#d4af37] shadow-[0_0_0_1px_#d4af37_inset]" : "hover:border-[#d4af37]/40"}`}>
                <p className={`text-[10px] font-mono uppercase tracking-[0.25em] ${active ? "text-[#d4af37]" : "text-gray-500"}`}>{s.label}</p>
                <p className={`font-display text-2xl mt-2 ${s.gold || active ? "text-[#d4af37]" : "text-white"}`}>{s.value}</p>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" strokeWidth={1.5}/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="iz-input pl-10" data-testid="lead-search"/>
          </div>
          {statusFilter !== "all" && (
            <button onClick={() => setStatusFilter("all")}
                    className="iz-btn-secondary text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5"
                    data-testid="clear-filter-btn">
              Filter: {statusFilter.replace(/_/g, " ")} · Clear ✕
            </button>
          )}
          <span className="text-xs font-mono uppercase tracking-widest text-gray-500">
            {monthLabel} · {filtered.length} / {scopedLeads.length}
          </span>
        </div>

        <div className="iz-card overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#262626] bg-[#0f0f0f]">
                {["Client","Contact","Source","Status","Assigned","Actions"].map(h => (
                  <th key={h} className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 py-4 px-5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-10 text-center text-gray-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="p-10 text-center text-gray-500">
                  {isMarketing ? "No marketing-handoff leads in this month." : "No leads in this month."}
                </td></tr>
              ) : filtered.map(l => {
                const assignee = l.assigned_to_user_id ? userById[l.assigned_to_user_id] : null;
                const isSelfAssigned = l.assigned_to_user_id && principal?.id && l.assigned_to_user_id === principal.id;
                return (
                  <tr key={l.id} className="border-b border-[#1a1a1a] hover:bg-[#0e0e0e]" data-testid={`ledger-row-${l.id}`}>
                    <td className="py-4 px-5">
                      <button onClick={() => setSelected(l)} className="text-left" data-testid={`open-lead-${l.id}`}>
                        <p className="text-sm text-white">{l.client_name}</p>
                        <p className="text-[11px] text-gray-500 font-mono">{l.email}</p>
                      </button>
                    </td>
                    <td className="py-4 px-5 text-xs font-mono text-gray-400">{l.phone}</td>
                    <td className="py-4 px-5 text-xs text-gray-400">{l.source || "—"}</td>
                    <td className="py-4 px-5">
                      <span className="inline-flex items-center gap-2 text-xs">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: STAGE_COLOR[l.status] }}/>
                        <span className="text-gray-300">{STAGE_LABEL[l.status]}</span>
                      </span>
                    </td>
                    <td className="py-4 px-5 text-xs text-gray-400" data-testid={`assignee-cell-${l.id}`}>
                      {assignee ? (
                        <span className="inline-flex items-center gap-1.5">
                          <UserIcon className="w-3 h-3"/>
                          {isSelfAssigned ? `${assignee.name} (You)` : assignee.name}
                        </span>
                      ) : (
                        <span className="text-[#d4af37]">Unassigned</span>
                      )}
                    </td>
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-2">
                        {isExec && !l.assigned_to_user_id && (
                          <button onClick={() => claim(l.id)} className="iz-btn-primary text-[11px] px-2.5 py-1 flex items-center gap-1" data-testid={`claim-${l.id}`}>
                            <HandCoins className="w-3 h-3"/> Assign to Self
                          </button>
                        )}
                        <button onClick={() => setSelected(l)} className="iz-btn-secondary text-[11px] px-2.5 py-1" data-testid={`view-${l.id}`}>Open</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isSM ? (
        <LeadCaptureDialog open={showAdd} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />
      ) : (
        <AddLeadModal open={showAdd} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />
      )}
      <LeadDetailDrawer lead={selected} onClose={() => setSelected(null)} onChanged={load} executives={executives}/>
    </div>
  );
}

export default function LeadLedgerPage() {
  return <LeadLedger variant="main" />;
}

export function MarketingLeadLedgerPage() {
  return <LeadLedger variant="marketing" />;
}
